/**
 * KanbanCardInlineChecklist — expandable per-tile checklist panel.
 *
 * Operator complaint 2026-05-15: *"within cards allow checklists too
 * where card turns into a description field where you can create a
 * mini checklist which is essentailly also expandable in view
 * structure"*.
 *
 * What this is:
 *   - The expandable PANEL that opens below a KanbanCard's body when
 *     the operator clicks the `{done}/{total} ✓` chip on the card
 *     footer.
 *   - Lazy: fetches `api.listChecklists(cardId)` only once on first
 *     open, then caches. Toggling re-uses the cached list.
 *   - Mutations: tick / add-item / delete-item / new-checklist are
 *     optimistic (UI updates immediately, server call happens in the
 *     background, UI rolls back on failure). Keeps Trello-style
 *     snappiness on the tile.
 *   - Compact density: NEVER renders. A compact tile is for stack
 *     density, not for in-place editing — the operator should open
 *     the drawer for full checklist work.
 *
 * What this is NOT:
 *   - The drawer's full checklist UI — the drawer renders titles,
 *     positions, drag-reorder, due-dates per item, and assignees.
 *     This component is the lightweight tile-level surface for
 *     "tick / add / delete" only.
 *   - Responsible for the chip itself. The chip lives in
 *     `KanbanCard.tsx`'s footer and renders as a `<button>` whose
 *     onClick toggles the `expanded` state passed to this component.
 *
 * I→O decomposition (rule 1.6):
 *   Inputs:  cardId, api, expanded, density, onChanged
 *   Outputs: render of <ul> per checklist; calls api.* on mutations
 *            and `onChanged?.()` after a successful write so the
 *            host can invalidate the lane's card-list cache (which
 *            in turn re-renders the chip with fresh
 *            `checklist_summary` totals from the BE).
 *
 * 280 lines incl. comments — under the 300-line ceiling. Tests in
 * `KanbanCardInlineChecklist.test.tsx`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Loader2, Plus, X as XIcon } from 'lucide-react'

import type { KanbanApi } from '../api'
import type {
  CardChecklistItemT,
  CardChecklistT,
} from '../types'

export interface KanbanCardInlineChecklistProps {
  cardId: string
  api: KanbanApi
  /** Controlled expansion state. Owned by the parent KanbanCard so
   *  the chip in the footer stays in sync with the panel below. */
  expanded: boolean
  /** Fired after EVERY successful mutation so the host can refetch
   *  the card list (and the parent KanbanCard re-renders its chip
   *  with the latest `checklist_summary.{total,done}` from the BE).
   *  Optional — drawerless tenants can skip it. */
  onChanged?: () => void
  /** Compact-density safety guard — the host may call us either way;
   *  we render nothing when compact so the tile stays tight. */
  density?: 'compact' | 'expanded' | 'large'
  /** HOVER-PREVIEW escape hatch (FEAT-2026-06-15-KANBAN-CARD-HOVER).
   *  Operator: "hover auto expands the card with its action sequences /
   *  checklists." On a compact board the chip toggle never appears, so
   *  the only way to reveal the checklist on a compact tile is the
   *  card's hover dwell. When the card sets this true (it only does so
   *  for a transient hover expansion), we bypass the compact null-guard
   *  and render the panel. Default false keeps the legacy "compact never
   *  renders" contract for every other caller. */
  allowCompact?: boolean
  /** Bump to force a re-fetch (FEAT-2026-06-20-KANBAN-CARD-AI-THREAD): when a
   *  checklist step is added from OUTSIDE this panel — e.g. the AI thread peek
   *  promoting an action item — the parent increments this so an already-open
   *  expand-down reflects the new step without a close/reopen. Each new value
   *  refetches if open, or invalidates the cache so the next open is fresh. */
  refreshToken?: number
}

/** What we render: a single normalised list of checklists with their
 *  items, keyed by checklist id. Live-mutated by optimistic updates;
 *  refreshed from the server after every successful network call. */
type LocalState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; checklists: CardChecklistT[] }

export function KanbanCardInlineChecklist({
  cardId,
  api,
  expanded,
  onChanged,
  density = 'expanded',
  allowCompact = false,
  refreshToken,
}: KanbanCardInlineChecklistProps) {
  const [state, setState] = useState<LocalState>({ kind: 'idle' })
  const lastTokenRef = useRef(refreshToken)
  const [addItemDraft, setAddItemDraft] = useState<Record<string, string>>({})
  // The "Add an item" inputs are one-per-checklist. Tracking drafts
  // in a flat record means typing into one doesn't blow away another.
  const addItemRef = useRef<HTMLInputElement | null>(null)

  const fetchChecklists = useCallback(async () => {
    setState({ kind: 'loading' })
    try {
      const list = await api.listChecklists(cardId)
      setState({ kind: 'ready', checklists: list ?? [] })
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }, [api, cardId])

  // Lazy fetch — only on first expansion. We DON'T re-fetch on
  // subsequent toggles because mutations refetch implicitly via the
  // optimistic update + onChanged path, and the host refetches the
  // card-list when a card opens/closes anyway.
  useEffect(() => {
    if (!expanded) return
    if (state.kind === 'idle') void fetchChecklists()
  }, [expanded, state.kind, fetchChecklists])

  // External-change refresh: when `refreshToken` changes (e.g. the AI thread
  // peek added a step), re-fetch if we've already loaded once — live if the
  // panel is open, else drop back to 'idle' so the next open fetches fresh.
  // The ref guard ensures this only fires on a genuine token change, never on
  // mount or unrelated re-renders.
  useEffect(() => {
    if (refreshToken === lastTokenRef.current) return
    lastTokenRef.current = refreshToken
    if (state.kind === 'idle') return // nothing cached yet; first open fetches
    if (expanded) void fetchChecklists()
    else setState({ kind: 'idle' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken])

  if ((density === 'compact' && !allowCompact) || !expanded) return null

  if (state.kind === 'loading' || state.kind === 'idle') {
    return (
      <div
        className="mt-1.5 flex items-center gap-1.5 border-t border-neutral-100 dark:border-zinc-700/70 pt-2 text-[11px] text-neutral-500 dark:text-zinc-400"
        style={{ color: 'var(--card-fg-muted)' }}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Loading checklist…</span>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div
        className="mt-1.5 border-t border-neutral-100 dark:border-zinc-700/70 pt-2 text-[11px] text-red-600 dark:text-red-400"
      >
        <span title={state.message}>Couldn't load checklist — click chip again to retry.</span>
      </div>
    )
  }

  const { checklists } = state
  if (checklists.length === 0) {
    return (
      <div
        className="mt-1.5 border-t border-neutral-100 pt-2 dark:border-zinc-700/70"
      >
        <NewChecklistInline
          ref={addItemRef}
          onCreate={async (title) => {
            try {
              const created = await api.createChecklist(cardId, title)
              setState({ kind: 'ready', checklists: [created] })
              onChanged?.()
            } catch (err) {
              setState({
                kind: 'error',
                message: err instanceof Error ? err.message : String(err),
              })
            }
          }}
        />
      </div>
    )
  }

  return (
    <div
      data-card-inline-checklist=""
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="mt-1.5 border-t border-neutral-100 pt-2 dark:border-zinc-700/70"
    >
      {checklists.map((cl) => (
        <ChecklistBlock
          key={cl.id}
          checklist={cl}
          draft={addItemDraft[cl.id] ?? ''}
          onDraftChange={(v) =>
            setAddItemDraft((m) => ({ ...m, [cl.id]: v }))
          }
          onToggleItem={async (item) => {
            const next = !item.complete
            // Optimistic — flip the item then call the API.
            setState({
              kind: 'ready',
              checklists: checklists.map((c) =>
                c.id !== cl.id
                  ? c
                  : {
                      ...c,
                      items: c.items.map((it) =>
                        it.id === item.id ? { ...it, complete: next } : it,
                      ),
                    },
              ),
            })
            try {
              await api.updateChecklistItem(item.id, { complete: next })
              onChanged?.()
            } catch {
              // Roll back on failure.
              setState({ kind: 'ready', checklists })
            }
          }}
          onDeleteItem={async (item) => {
            setState({
              kind: 'ready',
              checklists: checklists.map((c) =>
                c.id !== cl.id
                  ? c
                  : { ...c, items: c.items.filter((it) => it.id !== item.id) },
              ),
            })
            try {
              await api.deleteChecklistItem(item.id)
              onChanged?.()
            } catch {
              setState({ kind: 'ready', checklists })
            }
          }}
          onAddItem={async () => {
            const title = (addItemDraft[cl.id] ?? '').trim()
            if (!title) return
            // Clear the draft eagerly so the input doesn't double-fire.
            setAddItemDraft((m) => ({ ...m, [cl.id]: '' }))
            try {
              const created = await api.createChecklistItem({
                checklist: cl.id,
                title,
              })
              setState({
                kind: 'ready',
                checklists: checklists.map((c) =>
                  c.id !== cl.id ? c : { ...c, items: [...c.items, created] },
                ),
              })
              onChanged?.()
            } catch (err) {
              // Restore the draft so the operator doesn't lose their text.
              setAddItemDraft((m) => ({ ...m, [cl.id]: title }))
              setState({
                kind: 'error',
                message: err instanceof Error ? err.message : String(err),
              })
            }
          }}
        />
      ))}
    </div>
  )
}

// ----- Sub-components (kept in this file to avoid a tiny-file
//       sprawl; each one is <60 lines, the parent file stays under
//       the 300-line ceiling). -----

interface ChecklistBlockProps {
  checklist: CardChecklistT
  draft: string
  onDraftChange: (v: string) => void
  onToggleItem: (item: CardChecklistItemT) => void
  onDeleteItem: (item: CardChecklistItemT) => void
  onAddItem: () => void
}

function ChecklistBlock({
  checklist,
  draft,
  onDraftChange,
  onToggleItem,
  onDeleteItem,
  onAddItem,
}: ChecklistBlockProps) {
  return (
    <div
      data-checklist-id={checklist.id}
      className="mb-2 last:mb-0"
      style={{ color: 'var(--card-fg, currentColor)' }}
    >
      {checklist.title && checklist.title.toLowerCase() !== 'checklist' && (
        <div
          className="px-0.5 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-zinc-400"
          style={{ color: 'var(--card-fg-muted)' }}
        >
          {checklist.title}
        </div>
      )}
      <ul className="space-y-0.5">
        {checklist.items.map((item) => (
          <li
            key={item.id}
            data-checklist-item-id={item.id}
            data-complete={item.complete || undefined}
            className="group/cli flex items-center gap-1.5 rounded px-0.5 py-0.5 text-[12px] hover:bg-neutral-100/80 dark:hover:bg-zinc-800/40"
          >
            <button
              type="button"
              aria-label={item.complete ? 'Mark as not done' : 'Mark as done'}
              onClick={() => onToggleItem(item)}
              className={[
                'shrink-0 inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border transition-colors',
                item.complete
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-neutral-300 text-transparent hover:border-emerald-500 dark:border-zinc-600',
              ].join(' ')}
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </button>
            <span
              className={[
                'min-w-0 flex-1 truncate',
                item.complete ? 'line-through opacity-60' : '',
              ].join(' ')}
              title={item.title}
            >
              {item.title}
            </span>
            <button
              type="button"
              aria-label={`Delete "${item.title}"`}
              onClick={() => onDeleteItem(item)}
              className="shrink-0 rounded p-0.5 text-neutral-400 opacity-0 transition-opacity group-hover/cli:opacity-100 focus:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
            >
              <XIcon className="h-2.5 w-2.5" />
            </button>
          </li>
        ))}
      </ul>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onAddItem()
        }}
        className="mt-1 flex items-center gap-1"
      >
        <Plus className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden="true" />
        <input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="Add an item"
          className="min-w-0 flex-1 rounded bg-transparent px-1 py-0.5 text-[12px] text-neutral-700 placeholder:text-neutral-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:text-neutral-200 dark:focus:bg-neutral-900"
        />
      </form>
    </div>
  )
}

interface NewChecklistInlineProps {
  onCreate: (title: string) => Promise<void> | void
  ref?: React.Ref<HTMLInputElement>
}

const NewChecklistInline = function NewChecklistInline({
  onCreate,
}: NewChecklistInlineProps) {
  const [draft, setDraft] = useState('Checklist')
  const [submitting, setSubmitting] = useState(false)
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault()
        const title = draft.trim() || 'Checklist'
        setSubmitting(true)
        try {
          await onCreate(title)
        } finally {
          setSubmitting(false)
        }
      }}
      className="flex items-center gap-1"
    >
      <Plus className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden="true" />
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={submitting}
        className="min-w-0 flex-1 rounded bg-white px-1 py-0.5 text-[12px] text-neutral-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:bg-neutral-900 dark:text-neutral-200"
      />
      <button
        type="submit"
        disabled={submitting}
        className="rounded bg-blue-600 px-1.5 py-0.5 text-[11px] font-medium text-white disabled:opacity-60 hover:bg-blue-700"
      >
        Add
      </button>
    </form>
  )
}
